import CustomField from "components/common/CustomField";
import CustomLabel from "components/common/CustomLabel";
import DetailsPageTitle from "components/common/DetailsPageTitle";
import FormButtons from "components/common/FormButtons";
import Spinners from "components/common/Spinners";
import { globalSettingsSchema } from "components/common/Validation";
import { Form, Formik } from "formik";
import { InputSwitch } from "primereact/inputswitch";
import React, { useEffect, useState } from "react";
import { Col, Container, Row } from "react-bootstrap";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import {
  getGlobalSettings,
  updateGlobalSettings,
} from "store/actions/globalSettingSlice";
import { showAlert } from "store/reducers/alertReducer";

const initialValues = {
  cancelInfo: {
    checkBeforeCancel: false,
    valueInHours: null,
  },
  maintenanceMode: {
    iOSApp: null,
    androidApp: null,
    website: null,
  },
  forceAppUpdate: {
    iOSApp: null,
    androidApp: null,
  },
  vatInfo: {
    title: "",
    titleAr: "",
    value: null,
    registeredNo: "",
  },
};

const GlobalSettings = () => {
  const { t } = useTranslation();
  document.title = t("Global Settings | Work Space");

  const dispatch = useDispatch();

  const { isLoading, loading, globalSettings } = useSelector(
    (state) => state.GlobalSettings
  );

  const [formValues, setFormValues] = useState(initialValues);

  useEffect(() => {
    dispatch(getGlobalSettings());
  }, []);

  useEffect(() => {
    if (globalSettings.length > 0) {
      const { cancelInfo, maintenanceMode, forceAppUpdate, vatInfo } =
        globalSettings[0] || {};

      setFormValues({
        cancelInfo,
        maintenanceMode,
        forceAppUpdate,
        vatInfo,
      });
    }
  }, [globalSettings]);

  const handleOnSubmit = (values) => {
    const payload = {
      ...values,
      id: globalSettings[0]?._id,
    };
    dispatch(updateGlobalSettings(payload))
      .then((action) => {
        const { statusCode, success, message } = action?.payload;
        if (statusCode === 200 && success) {
          dispatch(showAlert({ text: message, type: "success" }));
        } else {
          dispatch(showAlert({ text: message, type: "error" }));
        }
      })
      .catch((err) => {});
  };

  return (
    <React.Fragment>
      <div className="page-content common_page">
        {loading ? (
          <Spinners />
        ) : (
          <Container fluid>
            <Row>
              <Formik
                initialValues={formValues}
                validationSchema={globalSettingsSchema}
                enableReinitialize
                onSubmit={handleOnSubmit}
              >
                {({ errors, values, touched, setFieldValue }) => (
                  <Form>
                    <DetailsPageTitle title={t("Global Settings")} />

                    <h2 className="fs_20 mt-3">
                      {t("Booking Cancellation Settings")}
                    </h2>
                    <div className="common_wrapper">
                      <Row>
                        <Col lg={6}>
                          <div className="form_field mb-0 d-flex align-items-center gap-3">
                            <InputSwitch
                              checked={values?.cancelInfo?.checkBeforeCancel}
                              onChange={(e) => {
                                const newValue =
                                  !values?.cancelInfo?.checkBeforeCancel;
                                setFieldValue(
                                  "cancelInfo.checkBeforeCancel",
                                  newValue
                                );
                                if (!newValue) {
                                  setFieldValue("cancelInfo.valueInHours", 0);
                                }
                              }}
                            />
                            <label
                              htmlFor="cancelInfo.checkBeforeCancel"
                              className="mb-0"
                            >
                              {t("Do you want to customized booking cancellation setting")}
                            </label>
                          </div>
                        </Col>
                        {values?.cancelInfo?.checkBeforeCancel && (
                          <Col lg={6}>
                            <div className="form_field">
                              <CustomLabel
                                htmlFor="cancelInfo.valueInHours"
                                label={t("Can cancel and get refund before booking start in : -- hours")}
                              />
                              <CustomField
                                name="cancelInfo.valueInHours"
                                type="number"
                                placeholder={t("In Hours")}
                                touched={touched}
                                errors={errors}
                              />
                            </div>
                          </Col>
                        )}
                      </Row>
                    </div>

                    <h2 className="fs_20">
                      {t("Maintenance Mode Settings")}
                    </h2>
                    <div className="common_wrapper">
                      <Row>
                        <Col lg={4}>
                          <div className="form_field mb-0 d-flex align-items-center gap-3">
                            <InputSwitch
                              checked={values?.maintenanceMode?.iOSApp}
                              onChange={(e) => {
                                setFieldValue(
                                  "maintenanceMode.iOSApp",
                                  !values?.maintenanceMode?.iOSApp
                                );
                              }}
                            />
                            <label
                              htmlFor="maintenanceMode.iOSApp"
                              className="mb-0"
                            >
                              {t("iOS App")}
                            </label>
                          </div>
                        </Col>
                        <Col lg={4}>
                          <div className="form_field mb-0 d-flex align-items-center gap-3">
                            <InputSwitch
                              checked={values?.maintenanceMode?.androidApp}
                              onChange={(e) => {
                                setFieldValue(
                                  "maintenanceMode.androidApp",
                                  !values?.maintenanceMode?.androidApp
                                );
                              }}
                            />
                            <label
                              htmlFor="maintenanceMode.androidApp"
                              className="mb-0"
                            >
                              {t("Android App")}
                            </label>
                          </div>
                        </Col>
                        <Col lg={4}>
                          <div className="form_field mb-0 d-flex align-items-center gap-3">
                            <InputSwitch
                              checked={values?.maintenanceMode?.website}
                              onChange={(e) => {
                                setFieldValue(
                                  "maintenanceMode.website",
                                  !values?.maintenanceMode?.website
                                );
                              }}
                            />
                            <label
                              htmlFor="maintenanceMode.website"
                              className="mb-0"
                            >
                              {t("Website")}
                            </label>
                          </div>
                        </Col>
                      </Row>
                    </div>

                    <h2 className="fs_20">{t("Force Update Settings")}</h2>
                    <div className="common_wrapper">
                      <Row>
                        <Col lg={4}>
                          <div className="form_field mb-0 d-flex align-items-center gap-3">
                            <InputSwitch
                              checked={values?.forceAppUpdate?.iOSApp}
                              onChange={(e) => {
                                setFieldValue(
                                  "forceAppUpdate.iOSApp",
                                  !values?.forceAppUpdate?.iOSApp
                                );
                              }}
                            />
                            <label
                              htmlFor="forceAppUpdate.iOSApp"
                              className="mb-0"
                            >
                              {t("iOS App")}
                            </label>
                          </div>
                        </Col>
                        <Col lg={4}>
                          <div className="form_field mb-0 d-flex align-items-center gap-3">
                            <InputSwitch
                              checked={values?.forceAppUpdate?.androidApp}
                              onChange={(e) => {
                                setFieldValue(
                                  "forceAppUpdate.androidApp",
                                  !values?.forceAppUpdate?.androidApp
                                );
                              }}
                            />
                            <label
                              htmlFor="forceAppUpdate.androidApp"
                              className="mb-0"
                            >
                              {t("Android App")}
                            </label>
                          </div>
                        </Col>
                      </Row>
                    </div>

                    <h2 className="fs_20">{t("VAT Settings")}</h2>
                    <div className="common_wrapper">
                      <Row>
                        <Col lg={6}>
                          <div className="form_field">
                            <CustomLabel
                              htmlFor="vatInfo.title"
                              label={t("English Title")}
                            />
                            <CustomField
                              name="vatInfo.title"
                              type="text"
                              placeholder={t("Enter English Title")}
                              touched={touched.vatInfo}
                              errors={errors.vatInfo}
                            />
                          </div>
                        </Col>
                        <Col lg={6}>
                          <div className="form_field">
                            <CustomLabel
                              htmlFor="vatInfo.titleAr"
                              label={t("Arabic Title")}
                            />
                            <CustomField
                              name="vatInfo.titleAr"
                              type="text"
                              placeholder={t("Enter Arabic Title")}
                              touched={touched.offerDetails}
                              errors={errors.offerDetails}
                              isArabic={true}
                            />
                          </div>
                        </Col>
                        <Col lg={6}>
                          <div className="form_field">
                            <CustomLabel
                              htmlFor="vatInfo.value"
                              label={t("Value in %")}
                            />
                            <CustomField
                              name="vatInfo.value"
                              type="number"
                              placeholder={t("Enter Value")}
                              touched={touched.offerDetails}
                              errors={errors.offerDetails}
                            />
                          </div>
                        </Col>
                        <Col lg={6}>
                          <div className="form_field">
                            <CustomLabel
                              htmlFor="vatInfo.registeredNo"
                              label={t("Registered No")}
                            />
                            <CustomField
                              name="vatInfo.registeredNo"
                              type="string"
                              placeholder={t("Enter Registered No")}
                              touched={touched.offerDetails}
                              errors={errors.offerDetails}
                            />
                          </div>
                        </Col>
                      </Row>
                    </div>
                    <div className="mt-5">
                      <FormButtons
                        isLoading={isLoading}
                        addBtnText={t("Update")}
                      />
                    </div>
                  </Form>
                )}
              </Formik>
            </Row>
          </Container>
        )}
      </div>
    </React.Fragment>
  );
};

export default GlobalSettings;
